import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import AddOutlined from '@mui/icons-material/AddOutlined';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import { DataTable, EmptyState, PageHeader, StatCard, StatusChip, type DataTableColumn } from '@routes-plan/ui';
import { CashBox } from '../../../models/cash-box';
import { useSelection } from '../../../components/context/SelectionContext';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { formatCurrency } from '../../../utils/format/currency';
import RowAction from '../components/RowAction';
import { ScreenTourButton } from '../../../components/help/ScreenHelp';
import OfficeRouteSelects from '../components/OfficeRouteSelects';
import { filterGridSx, pageSx, sectionSx, statGridSx } from '../components/layout';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const CashBoxList: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    const t = useT();
    setPageTitle('cashBoxes.pageTitle');
    const [box, setBox] = useState([]);
    const [offices, setOffices] = useState([]);
    const { office_id: officeId, setOffice_id: setOfficeId, setCollect_id: setRouteId } = useSelection();
    const navigate = useNavigate();

    // Cargar sedes
    useEffect(() => {
        const fetchData = async () => {
            showLoading();
            try {
                const officesResponse = await axios.get('offices');
                setOffices(officesResponse.data.data);
                await new Promise((resolve) => setTimeout(resolve, 1000));
            } catch (error) {
                //console.log('Error fetching data:', 'error');
            } finally {
                hideLoading();
            }
        };

        fetchData();
    }, []); // Solo se ejecuta una vez al montar el componente

    // Cargar cajas cuando se selecciona una sede
    useEffect(() => {
        if (officeId) {
            const fetchBoxData = async () => {
                showLoading();
                try {
                    const { data } = await axios.get(`offices/${officeId}/cash-boxes`);
                    setBox(data.data);
                    showAlert(t('cashBoxes.queryOk'), 'info');
                } catch (error) {
                    showAlert(t('common.loadError'), 'error');
                } finally {
                    hideLoading();
                }
            };

            fetchBoxData();
        }
    }, [officeId]); // Solo depende de officeId

    const rows = box.map((item: CashBox) => ({
        id: item.id as number,
        date: item.date,
        office: item.office?.name ?? '',
        routeName: item.route?.name ?? t('cashBoxes.mainCashBox'),
        isMain: !item.route,
        balance: Number(item.balance ?? 0),
        routeId: item.route?.id ?? '',
    }));
    type BoxRow = (typeof rows)[number];

    const openMovements = (row: BoxRow) => {
        const numericRouteId = typeof row.routeId === 'number' ? row.routeId : Number(row.routeId);
        // Si es caja principal, routeId viene vacío => dejamos la ruta seleccionada vacía.
        if (Number.isFinite(numericRouteId) && numericRouteId > 0) {
            setRouteId(String(numericRouteId));
        } else {
            setRouteId('');
        }
        navigate(`/loans/cash-box-expenses`);
    };

    const formatDate = (value: unknown) => {
        try {
            return format(parseISO(String(value)), 'yyyy-MM-dd HH:mm');
        } catch {
            return String(value ?? '');
        }
    };

    const totalBalance = rows.reduce((sum, row) => sum + row.balance, 0);
    const mainBalance = rows.filter((row) => row.isMain).reduce((sum, row) => sum + row.balance, 0);

    const columns: DataTableColumn<BoxRow>[] = [
        {
            id: 'routeName',
            header: t('cashBoxes.cashBox'),
            value: (row) => row.routeName,
            render: (row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {row.routeName}
                    {row.isMain ? <StatusChip label={t('cashBoxes.mainCashBox')} tone="info" /> : null}
                </Box>
            ),
        },
        { id: 'office', header: t('common.office'), value: (row) => row.office, hideBelow: 'md' },
        { id: 'date', header: t('cashBoxes.lastUpdate'), value: (row) => formatDate(row.date), hideBelow: 'sm' },
        {
            id: 'balance',
            header: t('cashBoxes.balance'),
            align: 'right',
            value: (row) => row.balance,
            render: (row) => formatCurrency(row.balance),
        },
    ];

    return (
        <Box sx={pageSx}>
            <PageHeader
                title={t('cashBoxes.listTitle')}
                description={t('cashBoxes.listDescription')}
                helpAction={<ScreenTourButton />}
                actions={
                    <Button variant="contained" startIcon={<AddOutlined />} component={NavLink} to="/loans/cash-box-expenses/new" data-tour="cash-boxes-new">
                        {t('cashBoxes.newMovement')}
                    </Button>
                }
            />
            <Paper variant="outlined" sx={sectionSx}>
                <Box sx={filterGridSx}>
                    <OfficeRouteSelects offices={offices} officeId={officeId} onOfficeChange={setOfficeId} required tourPrefix="cash-boxes" />
                </Box>
            </Paper>
            {rows.length > 0 ? (
                <Box sx={statGridSx} data-tour="cash-boxes-stats">
                    <StatCard label={t('cashBoxes.statTotal')} value={formatCurrency(totalBalance)} hint={t('cashBoxes.statBoxes', { count: rows.length })} />
                    <StatCard label={t('cashBoxes.mainCashBox')} value={formatCurrency(mainBalance)} />
                    <StatCard label={t('cashBoxes.statRoutes')} value={formatCurrency(totalBalance - mainBalance)} />
                </Box>
            ) : null}
            <DataTable<BoxRow>
                columns={columns}
                rows={rows}
                getRowId={(row) => row.id}
                searchable
                exportFileName={t('cashBoxes.exportFile')}
                ariaLabel={t('cashBoxes.listTitle')}
                tourId="cash-boxes"
                onRowClick={openMovements}
                rowActions={(row) => (
                    <RowAction label={t('cashBoxes.viewMovements')} icon={<ListAltOutlined fontSize="small" />} onClick={() => openMovements(row)} />
                )}
                emptyState={
                    <EmptyState
                        dense
                        icon={<AccountBalanceWalletOutlined />}
                        title={officeId ? t('cashBoxes.emptyTitle') : t('cashBoxes.selectOfficeTitle')}
                        description={officeId ? undefined : t('cashBoxes.selectOfficeDescription')}
                    />
                }
            />
        </Box>
    );
}

export default CashBoxList;
